import React, { useEffect, useState } from 'react'
import FormTile from 'src/components/common/containers/FormTile'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'
import FloRadioGroup from 'src/components/common/form-elements/FloRadioGroup'
import { useCustomizeUIContext } from 'src/lib/contexts/CustomizeUIProvider'
import { useLocale } from 'src/lib/hooks/useLocal'
import { Switch } from '@mui/material'
import FloGenericDialog from 'src/components/common/feedback-and-dialog/GenericDialog'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { setMerchantAttributes } from 'src/lib/core/api-methods'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { ArrowLeft, ChevronDown, ShoppingCart } from 'react-feather'
import { OrderSummaryVisibilityType } from 'src/assets/CustomizeUIConfig'
import dynamic from 'next/dynamic'
import Pill from 'src/components/common/ui-preview/skeletons/Pill'
import { OrderSummaryConfigType, OrderSummaryStyleType } from 'src/lib/types/customizeUI'

import HeaderRadioItems from '../sub-components/order-summary-card/HeaderRadioItems.tsx'
import { useCustomizeUI } from 'src/lib/access-permission-hooks/useCustomizeUI'
import { RadioItemConfigType } from 'src/lib/types/random.js'
import { ExitCouponTile } from './exit-coupon/ExitCouponTile'

interface OrderSummaryEditor {
  type: 'ONE_PAGE_CHECKOUT' | 'PRICE_STRIKE_THROUGH' | 'ORDER_SUMMARY' | 'EXIT_COUPON'
  canEdit: boolean
}

const orderSummaryOpenRadioItemsConfig = (orderSummaryConfig: OrderSummaryConfigType): RadioItemConfigType[] => [
  {
    label: (
      <HeaderRadioItems
        type='TILE'
        orderSummaryConfig={{
          isOrderSummaryOpen: orderSummaryConfig.isOrderSummaryOpen,
          isQuantityEditable: orderSummaryConfig.isQuantityEditable,
          isPriceStrikeThroughEnabled: orderSummaryConfig.isPriceStrikeThroughEnabled,
          showOrderSummaryAs: orderSummaryConfig.showOrderSummaryAs,
          isOnePageCheckoutEnable: orderSummaryConfig.isOnePageCheckoutEnable
        }}
      />
    ),
    valueType: OrderSummaryVisibilityType.TILE
  },
  {
    label: (
      <>
        <HeaderRadioItems
          type='HEADER'
          orderSummaryConfig={{
            isOrderSummaryOpen: orderSummaryConfig.isOrderSummaryOpen,
            isQuantityEditable: orderSummaryConfig.isQuantityEditable,
            isPriceStrikeThroughEnabled: orderSummaryConfig.isPriceStrikeThroughEnabled,
            showOrderSummaryAs: orderSummaryConfig.showOrderSummaryAs,
            isOnePageCheckoutEnable: orderSummaryConfig.isOnePageCheckoutEnable
          }}
        />
      </>
    ),
    valueType: OrderSummaryVisibilityType.HEADER
  }
]

const OrderSummaryEditor: React.FC<OrderSummaryEditor> = ({ type, canEdit }) => {
  const { t } = useLocale()
  const logoUrl = useCustomizeUIContext(context => context.state.brandLogoURL)
  const orderSummaryConfig = useCustomizeUIContext(context => context.state.orderSummaryConfig)
  const setOrderSummaryConfig = useCustomizeUIContext(context => context.actions.setOrderSummaryConfigData)
  const [showDialog, setShowDialog] = useState(false)
  const {
    getters: { merchant },
    actions: { mutateMerchantAttributes }
  } = useMerchant()
  const { setLoaderProperties } = useLoader()

  const radioItemsConfig: RadioItemConfigType[] = orderSummaryOpenRadioItemsConfig(orderSummaryConfig)
  const ImageContainer = dynamic(() => import(`../../common/ImageContainer`), { ssr: false })

  switch (type) {
    case 'ONE_PAGE_CHECKOUT':
      return (
        <FormTile
          title={t('one_page_checkout_title')}
          subtitle={t('one_page_checkout_subtitle')}
          endComponent={
            <div className='flex flex-col justify-center items-center'>
              <FloCheckbox
                isDisabled={!canEdit}
                type='SWITCH'
                value={orderSummaryConfig.isOnePageCheckoutEnable}
                setValue={(checked: boolean) => {
                  setShowDialog(true)
                }}
              />
            </div>
          }
        >
          <FloGenericDialog
            width={700}
            jobButtonText={'Save Changes'}
            closeButtonText={'Cancel'}
            content={
              <>
                {!orderSummaryConfig.isOnePageCheckoutEnable
                  ? t('one_page_optimised_checkout')
                  : t('one_page_remove_optimised_checkout')}
              </>
            }
            title={!orderSummaryConfig.isOnePageCheckoutEnable ? 'Optimise checkout' : 'Remove optimised checkout'}
            isOpen={showDialog}
            handleClose={() => setShowDialog(false)}
            handleJob={async () => {
              const nextValue = !orderSummaryConfig.isOnePageCheckoutEnable
              try {
                setLoaderProperties({ isLoaderVisible: true, message: t('saving') })
                const response: any = await setMerchantAttributes(
                  { attributes: { is_one_page_checkout: nextValue } },
                  merchant?.accountId ?? ''
                )
                if (response?.error) {
                  errorToast(response?.error)
                } else {
                  setOrderSummaryConfig({
                    ...orderSummaryConfig,
                    isOnePageCheckoutEnable: nextValue,
                    isOrderSummaryOpen: false
                  })
                  mutateMerchantAttributes()
                  successToast(t('alert_customization_saved'))
                }
              } catch (e) {
                errorToast(t('alert_customization_failed'))
                console.error(e)
              } finally {
                setLoaderProperties({ isLoaderVisible: false })
                setShowDialog(false)
              }
            }}
          />
        </FormTile>
      )
    case 'PRICE_STRIKE_THROUGH':
      return (
        <FormTile title={t('price_strike_through_title')} subtitle={t('price_strike_through_subtitle')}>
          <div className='-mt-1'>
            <FloCheckbox
              isDisabled={!canEdit}
              value={orderSummaryConfig.isPriceStrikeThroughEnabled}
              label={t('show_strike_through_checkbox')}
              setValue={(checked: boolean) => {
                setOrderSummaryConfig({ ...orderSummaryConfig, isPriceStrikeThroughEnabled: checked })
              }}
            />
          </div>
        </FormTile>
      )
    case 'ORDER_SUMMARY':
      return (
        <div>
          <FormTile title={t('order_summary')}>
            {!orderSummaryConfig.isOnePageCheckoutEnable && (
              <div className='flex items-center w-full py-2'>
                <div className='flex flex-row justify-between gap-3 w-full'>
                  {radioItemsConfig.map((option, index) => (
                    <div
                      key={option.valueType}
                      className='cursor-pointer flex-1'
                      onClick={() => {
                        if (!canEdit) return
                        setOrderSummaryConfig({
                          ...orderSummaryConfig,
                          showOrderSummaryAs: option.valueType,
                          ...(option.valueType === 'HEADER' ? { isOrderSummaryOpen: false } : {})
                        })
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className='-mt-3'>
              <FloCheckbox
                isDisabled={!canEdit}
                value={orderSummaryConfig.isQuantityEditable}
                label={
                  <div className='flex flex-col pt-4'>
                    <span>{t('item_qty_editor_checkbox')}</span>
                    <span className='text-gray-dark text-xs pt-1'>{t('item_qty_editor_checkbox_description')}</span>
                  </div>
                }
                setValue={(checked: boolean) => {
                  setOrderSummaryConfig({ ...orderSummaryConfig, isQuantityEditable: checked })
                }}
              />
            </div>
            {!orderSummaryConfig.isOnePageCheckoutEnable && (
              <FloCheckbox
                isDisabled={canEdit ? orderSummaryConfig.showOrderSummaryAs === 'HEADER' : true}
                value={orderSummaryConfig.isOrderSummaryOpen}
                label={t('expand_order_summary')}
                setValue={(checked: boolean) => {
                  setOrderSummaryConfig({
                    ...orderSummaryConfig,
                    isOrderSummaryOpen: checked
                  })
                }}
              />
            )}
          </FormTile>
        </div>
      )
    case 'EXIT_COUPON':
      return <ExitCouponTile canEdit={canEdit} />
  }
}

export default OrderSummaryEditor
